/**
 * Form component for adding or editing configuration parameters.
 *
 * @param {TDrawerProps} props - Contains properties such as id, onCloseDrawer, and onRefreshList.
 * @returns {JSX.Element} - Returns the form layout with configuration parameter fields.
 *
 * This component fetches specific configuration parameter data if an id is provided.
 * It handles form submission to add or edit configuration information.
 * Displays success or error toast messages based on the operation result.
 */

import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Row,
  Col,
  Select,
  DatePicker,
  Checkbox,
  Upload,
} from "antd";
import Input from "../../../components/input";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { eDocType, eResultCode, eRoleUniqueId } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  GetUserList,
  GetProjectCategoryList,
  AddEditProject,
  GetSpecificProject,
  GetAllClients,
  AddEditClient,
} from "../../../utils/api.constant";
import { TProject, TUser } from "../../../types/config";
import {
  CheckOutlined,
  CloseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import styles from "./projectManagement.module.css";
import { PageSize } from "../../../utils/constants";
import dayjs from "dayjs";
// type DrawerProps = {
//   id: number;
//   onCloseDrawer: () => void;
//   onRefreshList: () => void;
// };
type Client = {
  clientId: string;
  clientName: string;
};

type ProjectCategory = {
  projectCategoryId: string;
  projectCategoryName: string;
};

type Employee = {
  employeeId: string;
  employeeName: string;
};
type TDrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};
const { Option } = Select;
/**
 * Form component for adding or editing configuration parameters.

 */

export default function AddEditProjectForm(props: TDrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [projectCategories, setProjectCategories] = useState<ProjectCategory[]>(
    [],
  );
  // const [isRecurring, setIsRecurring] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [showNewClientFields, setShowNewClientFields] = useState(false);

  // Hook to handle on load operations
  useEffect(() => {
    fetchClients();
    fetchEmployees();
    fetchProjectCategories();
    fetchSpecificProject();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await post(
        GetAllClients,
        {},
        undefined,
        process.env.REACT_APP_PUBLIC_MOM_API_BASEURL,
      );

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        setClients(
          response.data.map((item: { id: string; name: string }) => ({
            clientId: item.id,
            clientName: item.name,
          })),
        );
      } else {
        console.error(
          "Error fetching clients:",
          response.dataResponse.description,
        );
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };
  const fetchEmployees = async () => {
    try {
      const response = await post(GetUserList, {
        data: {
          pageSize: PageSize,
          currentPage: 1,
        },
      });
      const { data, dataResponse } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setEmployees(
          data.map((item: any) => ({
            employeeId: item.id,
            employeeName: item.fullName,
          })),
        );
      } else {
        console.error("Error fetching employees:", dataResponse.description);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };
  const fetchProjectCategories = async () => {
    try {
      const response = await post(GetProjectCategoryList, {
        data: { pageSize: PageSize, currentPage: 1 },
      });
      const { data, dataResponse } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setProjectCategories(
          data.map((item: any) => ({
            projectCategoryId: item.id,
            projectCategoryName: item.name,
          })),
        );
      } else {
        console.error(
          "Error fetching project categories:",
          dataResponse.description,
        );
      }
    } catch (error) {
      console.error("Error fetching project categories:", error);
    }
  };

  const fetchSpecificProject = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response = await post(GetSpecificProject, payload);
        const { data } = response;
        const project = data;
        const teamMemberIds =
          project.teamMembers?.map((member: { id: string }) => member.id) || [];

        // Map existing project documents to file
        const existingAttachments =
          project.projectdocuments?.map((doc: any, index: number) => ({
            uid: `${index}-${doc.docName}`,
            name: doc.docName,
            status: "done",
            url: `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}/${doc.docURL}`,
            thumbUrl: `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}/${doc.docURL}`,
          })) || [];

        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const formValues = {
            ...project,
            clientId: project.clientName,
            startDate: project.startDate ? dayjs(project?.startDate) : "",
            endDate: project.endDate ? dayjs(project?.endDate) : "",
            teamMembers: teamMemberIds,
            isRecurring: project.isRecurring,
            attachments: existingAttachments,
          };

          form.setFieldsValue(formValues);
          // setIsRecurring(data?.isRecurring === 1 ? true : false);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onFinish = async (values: TProject) => {
    try {
      let clientId = values.clientId;

      // If new client fields are shown and filled, create client first
      if (showNewClientFields) {
        const clientName = form.getFieldValue("newClientName");
        const clientEmail = form.getFieldValue("newClientEmail");
        const clientMobile = form.getFieldValue("newClientMobile");
        const clientCompanyName = form.getFieldValue("newClientCompanyName");

        if (
          !clientName ||
          !clientEmail ||
          !clientMobile ||
          !clientCompanyName
        ) {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: "Please fill in all client fields",
          });
          return;
        }

        setIsLoading(true);

        const clientPayload = {
          data: {
            id: "0",
            name: clientName,
            email: clientEmail,
            mobile: clientMobile,
            companyName: clientCompanyName,
          },
        };

        try {
          const clientResponse = await post(
            AddEditClient,
            clientPayload,
            undefined,
            process.env.REACT_APP_PUBLIC_MOM_API_BASEURL,
          );

          if (clientResponse.dataResponse.returnCode === eResultCode.SUCCESS) {
            clientId = clientResponse.data.id;
            onShowToast({
              type: ToastType.success,
              title: <CheckOutlined />,
              position: ToastOpen.centerTop,
              content: "Client created successfully",
            });
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.centerTop,
              content: clientResponse.dataResponse.description,
            });
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error("Error creating client:", error);
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: "Failed to create client",
          });
          setIsLoading(false);
          return;
        }
      }

      // Now create the project with the clientId
      const {
        attachments,
        newClientName,
        newClientEmail,
        newClientMobile,
        newClientCompanyName,
        ...restvalues
      } = values as any;
      const payload = {
        data: {
          ...restvalues,
          clientId,
          id: props.id ? props.id : 0,
          // isRecurring: values.isRecurring ? 1 : 0,
          endDate: dayjs(values.endDate),
          startDate: dayjs(values.startDate),
        },
      };

      if (!isLoading) {
        setIsLoading(true);
      }

      const response = await post(AddEditProject, payload);

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        try {
          const formData = new FormData();
          values.attachments?.forEach((file: any) => {
            formData.append("files", file.originFileObj);
          });
          formData.append("projectId", response.data?.newProject?.id);
          formData.append("docType", eDocType.PROFILE_IMAGE.toString());
          const uploadResponse = await post("/uploadMedia", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          if (uploadResponse.dataResponse.returnCode === eResultCode.SUCCESS) {
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.centerTop,
              content: uploadResponse.dataResponse.description,
            });
          }
        } catch (error) {
          console.error("Error:", error);
        }
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: props.id
            ? "Project updated successfully"
            : "Project added successfully",
        });

        form.resetFields();
        setShowNewClientFields(false);
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        layout="vertical"
        // onValuesChange={(changedValues) => {
        //   if (changedValues.isRecurring !== undefined) {
        //     setIsRecurring(changedValues.isRecurring);
        //     if (changedValues.isRecurring) {
        //       form.setFieldValue("endDate", "");
        //     }
        //   }
        // }}
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={props.onCloseDrawer}
          />
        </FormHeader>

        <FormContent>
          {/* <h1 className={styles.project_heading}>Add Project</h1> */}
          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="clientId"
                label="Client"
                rules={[
                  {
                    required: !showNewClientFields,
                    message: "Please select a client",
                  },
                ]}
              >
                <Select
                  placeholder="Select client"
                  onSelect={(value) => {
                    if (value === "ADD_NEW") {
                      setShowNewClientFields(true);
                      form.setFieldValue("clientId", undefined);
                    } else {
                      setShowNewClientFields(false);
                    }
                  }}
                >
                  <Option key="ADD_NEW" value="ADD_NEW">
                    + Add New Client
                  </Option>
                  {clients.map((client) => (
                    <Option key={client.clientId} value={client.clientId}>
                      {client.clientName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Project Name"
                rules={[
                  { required: true, message: "Please enter project name" },
                ]}
              >
                <Input placeholder="Enter project name" />
              </Form.Item>
            </Col>
          </Row>

          {showNewClientFields && (
            <>
              <Row gutter={16} className="formRowHeight">
                <Col span={12}>
                  <Form.Item
                    name="newClientName"
                    label="Client Name"
                    rules={[
                      {
                        required: showNewClientFields,
                        message: "Please enter client name",
                      },
                    ]}
                  >
                    <Input placeholder="Enter client came" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="newClientEmail"
                    label="Client Email"
                    rules={[
                      {
                        required: showNewClientFields,
                        message: "Please enter client email",
                      },
                      {
                        type: "email",
                        message: "Please enter a valid email",
                      },
                    ]}
                  >
                    <Input placeholder="Enter client email" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16} className="formRowHeight">
                <Col span={12}>
                  <Form.Item
                    name="newClientMobile"
                    label="Client Mobile"
                    rules={[
                      {
                        required: showNewClientFields,
                        message: "Please enter client mobile",
                      },
                      {
                        pattern: /^[0-9]{10}$/, // Basic 10-digit mobile number validation
                        message: "Please enter a valid 10-digit mobile number",
                      },
                    ]}
                  >
                    <Input placeholder="Enter client mobile" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="newClientCompanyName"
                    label="Company Name"
                    rules={[
                      {
                        required: showNewClientFields,
                        message: "Please enter company name",
                      },
                    ]}
                  >
                    <Input placeholder="Enter company name" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Row gutter={16} className="formRowHeight">
            <Col xs={24} sm={12}>
              <Form.Item
                name="projectCategoryId"
                label="Project Category"
                rules={[
                  {
                    required: true,
                    message: "Please select a project category",
                  },
                ]}
              >
                <Select placeholder="Select category">
                  {projectCategories.map((category) => (
                    <Option
                      key={category.projectCategoryId}
                      value={category.projectCategoryId}
                    >
                      {category.projectCategoryName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="teamMembers"
                label="Team Members"
                rules={[
                  {
                    required: true,
                    message: "Please select at least one team member",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select team members"
                  style={{ width: "100%" }}
                  maxTagCount={1}
                  filterOption={(input, option) =>
                    (option?.label as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  showSearch
                >
                  {employees.map((employee) => (
                    <Option
                      key={employee.employeeId}
                      value={employee.employeeId}
                      label={employee.employeeName}
                    >
                      {employee.employeeName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} className="formRowHeight">
            <Col xs={24} sm={12}>
              <Form.Item
                name="startDate"
                label="Start Date"
                rules={[
                  { required: true, message: "Please select a start date" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Select start date"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="endDate"
                label="End Date"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(
                          new Error("End Date is required"),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Select end date"
                  // disabled={isRecurring}
                />
              </Form.Item>
              {/* <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Form.Item
                  name='isRecurring'
                  valuePropName='checked'
                  style={{ position: "relative", bottom: "28px" }}
                >
                  <Checkbox
                    onChange={(e) => {
                      form.setFieldsValue({ isRecurring: e.target.checked });
                    }}
                  >
                    Recurring Project
                  </Checkbox>
                </Form.Item>
              </div> */}
            </Col>
          </Row>
          <Row
            gutter={16}
            className="formRowHeight"
            style={{ marginTop: "20px" }}
          >
            <Col span={24}>
              <Form.Item
                name="attachments"
                label="Attachments"
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
              >
                <Upload
                  name="files"
                  listType="picture-card"
                  multiple={true}
                  beforeUpload={() => false}
                  onChange={({ fileList }) => {
                    form.setFieldValue("attachments", fileList);
                  }}
                >
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <Button
            type="default"
            disabled={isLoading}
            size="large"
            onClick={props.onCloseDrawer}
            style={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={form.submit}
            size="large"
            disabled={isLoading}
            style={{ flex: 1 }}
          >
            {props.id ? "Update" : "Add"}
          </Button>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
